import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Colour, Status, TripInfo, BusInfo, emvHeartbeatInfo } from 'src/app/app.model';
import { StatusDialog } from '../statusdialog/statusdialog.component';
import { isWithinLast } from 'src/app/Utils/date-utils';

@Component({
  selector: 'status-bar',
  templateUrl: './statusbar.component.html',
  styleUrls: ['./statusbar.component.scss'],
  imports: [MatIconModule]
})
export class StatusBar implements OnInit {
  @Input() status: Status;
  @Input() tripInfo: TripInfo;
  @Input() emvHeartbeats: emvHeartbeatInfo[];

  readonly dialog = inject(MatDialog);
  windowWidth: number;

  /** Primary colours */
  private readonly _GREY = '#F2F2F2';
  private readonly _RED = '#E41326';
  private readonly _GREEN = '#41AD49';
  private readonly _ORANGE = '#F77D2B';
  private readonly _BLUE = '#0076FF';

  /** Secondary colour */
  private readonly _SECONDARY_GREY = '#8A8A8A';
  private readonly _SECONDARY_WHITE = '#FFFFFF';

  constructor() {}

  ngOnInit() {
    this.windowWidth = window.innerWidth;
  }

  desktopSize(): boolean {
    return this.windowWidth > 768;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.windowWidth = window.innerWidth;
  }

  getColourFromStatus(type: string): string {
    let colour;

    if (type == 'BDC') {
      colour = this.colourToHex(this.status.bdcColour);
    }

    if (type == 'CP') {
      colour = this.colourToHex(this.status.cpColour);
    }

    if (type == 'Location') {
      colour = this.colourToHex(this.status.locationColour);
    }

    if (type == 'EMV') {
      colour = this.colourToHex(this.status.emvColour);
    }

    return colour;
  }

  getSecondaryColour(type: string): string {
    let colour;

    if (type == 'BDC') {
      colour = this.secondaryColourHex(this.status.bdcColour);
    }

    if (type == 'CP') {
      colour = this.secondaryColourHex(this.status.cpColour);
    }

    if (type == 'Location') {
      colour = this.secondaryColourHex(this.status.locationColour);
    }

    if (type == 'EMV') {
      colour = this.secondaryColourHex(this.status.emvColour);
    }

    return colour;
  }

  get messageStatusColour() {
    const green = '#41AD49';
    const orange = '#F77D2B';
    const red = '#E41326';
    const grey = '#999999';
    const blue = '#0076FF';

    if (this.status.symbolColour === 1) {
      return green;
    }
    if (this.status.symbolColour === 2) {
      return orange;
    }
    if (this.status.symbolColour === 3) {
      return red;
    }
    if (this.status.symbolColour === 4) {
      return grey;
    }

    if (this.status.symbolColour === 5) {
      return blue;
    }

    return '#000000';
  }

  private colourToHex(colour: Colour): string {
    if (colour == Colour.GREEN) {
      return this._GREEN;
    }

    if (colour == Colour.RED) {
      return this._RED;
    }

    if (colour == Colour.ORANGE) {
      return this._ORANGE;
    }

    if (colour == Colour.GREY) {
      return this._GREY;
    }

    if (colour == Colour.BLUE) {
      return this._BLUE;
    }
  }

  private secondaryColourHex(backgroundColour: Colour): string {
    if (backgroundColour == Colour.GREY) {
      return this._SECONDARY_GREY;
    } else {
      return this._SECONDARY_WHITE;
    }
  }

  cpColorDialog() {
    //show popup explaining colours
    this.dialog.open(StatusDialog, {
      height: this.desktopSize() ? '475px' : '400px',
      width: '600px',
      data: {
        title: 'Cradlepoint Statuses',
        subtitle: 'The following colours for Cradlepoint statuses represents:',
        status: [
          { color: 'E41326', explanation: 'No CP heartbeats found' },
          { color: 'F77D2B', explanation: 'Stale CP heartbeats found' },
          { color: '41AD49', explanation: 'Good CP heartbeats found' },
          { color: '0076FF', explanation: 'No vehicle ignition but CP on' },
          { color: 'F2F2F2', explanation: 'Failed to retrieve data' }
        ]
      }
    });
  }

  bdcColorDialog() {
    //show popup explaining colours
    this.dialog.open(StatusDialog, {
      height: '400px',
      width: '600px',
      data: {
        title: 'BDC Statuses',
        subtitle: 'The following colours for BDC statuses represents:',
        status: [
          { color: 'F77D2B', explanation: 'No BDC heartbeat. Normal when bus is not on active trip.' },
          { color: '41AD49', explanation: 'Both CP heartbeat and BDC heartbeats found' },
          { color: 'F2F2F2', explanation: 'Failed to retrieve data' }
        ]
      }
    });
  }

  locationColorDialog() {
    //show popup explaining colours
    this.dialog.open(StatusDialog, {
      height: '400px',
      width: '600px',
      data: {
        title: 'GPS Statuses',
        subtitle: 'The following colours for GPS statuses represents:',
        status: [
          { color: 'E41326', explanation: 'Missing GPS data from CP and/or BDC' },
          { color: 'F77D2B', explanation: 'Difference between CP GPS and BDC GPS > 35m' },
          { color: '41AD49', explanation: 'Both CP GPS and BDC GPS found' },
          { color: 'F2F2F2', explanation: 'Stale GPS or missing either CP GPS or BDC GPS' }
        ]
      }
    });
  }

  emvColorDialog() {
    //show popup explaining colours
    const heartbeats = this.emvHeartbeats ?? [];

    const status = heartbeats.length
      ? heartbeats.map((hb) => {
          const ONE_HOUR = 60 * 60 * 1000;
          const healthy = hb.aipaServerStatus === '1' && hb.aipaUpdateStatus === '1' && isWithinLast(hb.time, ONE_HOUR);

          return {
            color: healthy ? '41AD49' : 'F77D2B',
            explanation:
              `Device ${hb.validatorDevice} (pos ${hb.validatorPosition}) | ` +
              `Vehicle ${hb.vehicleId} | ` +
              `AiPA Server Status ${hb.aipaServerStatus} | AiPA Update Status ${hb.aipaUpdateStatus} | ` +
              `Last seen: ${this.formatTime(hb.time)}`
          };
        })
      : [
          { color: 'E41326', explanation: 'No EMV heartbeat data available' }
        ];

    this.dialog.open(StatusDialog, {
      height: '400px',
      width: '600px',
      data: {
        title: 'EMV Statuses',
        subtitle: `Showing EMV devices`,
        status
      }
    });
  }

  private formatTime(isoTime: string): string {
    return new Date(isoTime).toLocaleString('en-NZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
